import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { deleteTreatmentPlan } from 'api/TreatmentPlan';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { ia, iaRa } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Buttons/Button';

import DeleteTreatmentPlanConfirm from './DeleteTreatmentPlanConfirm';
import {
  ERROR_MESSAGE_TP_DELETE,
  SUCCESS_MESSAGE_TP_DELETE,
  getTreatmentPlanParams
} from './lib/helpers';

const ButtonActions = ({ selectedRows, setSelectedRows }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { id } = useParams();
  const { limit, page, sort, filters } = useTableContext();

  const navigate = useNavigate();
  const location = useLocation();
  const fromPracticeIQueue = location.pathname.includes('practice-i-queue');
  const params = getTreatmentPlanParams({ id, filters, page, sort, limit, fromPracticeIQueue });

  const selectedTPs = iaRa(selectedRows).map((row) => row?.data?.id);
  const queryClient = useQueryClient();

  const mutateDelete = useCustomMutation({
    mutationFn: () => deleteTreatmentPlan(navigate, { treatmentIds: selectedTPs }),
    successMessage: SUCCESS_MESSAGE_TP_DELETE({ itemsAffected: selectedTPs?.length }),

    errorMessage: ERROR_MESSAGE_TP_DELETE({ itemsAffected: selectedTPs?.length }),
    onSuccess: () => {
      setSelectedRows([]);
      setShowConfirmation(false);
      queryClient.invalidateQueries(['treatmentPlan', [params]]);
    }
  });

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          data-qa="delete-selected-treatment-plan"
          onClick={() => setShowConfirmation(true)}
          text="Delete"
          type="link"
          loading={mutateDelete?.isLoading}
          disabled={!ia(selectedTPs)}
          color="danger"
          icon="trash-mono"
          className="!text-danger-500"
          iconSize={18}
        />
      </div>

      {showConfirmation && (
        <DeleteTreatmentPlanConfirm
          treatmentPlanToDelete={showConfirmation}
          loading={mutateDelete?.isLoading}
          handleClose={() => setShowConfirmation(false)}
          handleDeleteTreatmentPlan={mutateDelete.mutate}
        />
      )}
    </>
  );
};

export default ButtonActions;
